/**
 * Care Attendant / shift service ∩ customer selection (frontend).
 * Mirrors NemaClub-BE/utils/caregiverServiceMatch.js
 */

import { mergeAttendantPackageMap } from "../config/attendantCareHubConfig";

export function resolveAttendantPackageIdFromSelection({
  packageKey = "",
  serviceId = "",
} = {}) {
  const key = String(packageKey || "").trim().toLowerCase();
  const sid = String(serviceId || "").trim().toLowerCase();
  if (key === "hygiene_personal" || sid === "hygiene_personal") return "hygiene_personal";
  if (
    key === "companion" ||
    key.startsWith("companion:") ||
    sid.startsWith("companion_")
  ) {
    return "companion";
  }
  return null;
}

function isOfferedRow(row) {
  if (!row || typeof row !== "object") return false;
  if (row.offered === false || row.offered === "false" || row.offered === 0) return false;
  if (row.enabled === false || row.active === false) return false;
  return true;
}

function isShiftPricingActive(row) {
  if (!row || typeof row !== "object") return false;
  if (row.active === false || row.enabled === false || row.offered === false) return false;
  const price = Number(row.priceInr ?? row.price);
  return Number.isFinite(price) && price > 0;
}

function getRawCaregiverAttendantPackages(caregiver) {
  if (!caregiver || typeof caregiver !== "object") return null;
  if (caregiver.packages && typeof caregiver.packages === "object") return caregiver.packages;
  if (caregiver.attendantPackages && typeof caregiver.attendantPackages === "object") {
    return caregiver.attendantPackages;
  }
  const fromExt =
    caregiver.profileExtended?.attendantCareHub?.packages ||
    caregiver.profile_extended?.attendantCareHub?.packages;
  return fromExt && typeof fromExt === "object" ? fromExt : null;
}

/** Missing Pricing & Plans → all catalog packages offered (platform defaults). */
export function getCaregiverAttendantPackages(caregiver) {
  return mergeAttendantPackageMap(getRawCaregiverAttendantPackages(caregiver));
}

export function getCaregiverShiftPricing(caregiver) {
  if (!caregiver || typeof caregiver !== "object") return null;
  if (caregiver.shiftPricing && typeof caregiver.shiftPricing === "object") {
    return caregiver.shiftPricing;
  }
  const fromExt =
    caregiver.profileExtended?.shiftPricing || caregiver.profile_extended?.shiftPricing;
  return fromExt && typeof fromExt === "object" ? fromExt : null;
}

/**
 * Strict ∩: package must exist on caregiver Pricing & Plans and be offered.
 */
export function caregiverOffersAttendantPackage(caregiver, { packageKey, serviceId } = {}) {
  const packId = resolveAttendantPackageIdFromSelection({ packageKey, serviceId });
  if (!packId) return true;
  const packages = getCaregiverAttendantPackages(caregiver);
  const row = packages[packId];
  if (!row || typeof row !== "object") return false;
  return isOfferedRow(row);
}

export function resolveOfferedShiftTypesFromPricing(caregiver) {
  const out = new Set();
  const packages = getCaregiverAttendantPackages(caregiver);
  if (packages) {
    if (isOfferedRow(packages.hygiene_personal) || isOfferedRow(packages.companion)) {
      out.add("hourly");
    }
  }
  const pricing = getCaregiverShiftPricing(caregiver);
  if (pricing) {
    if (isShiftPricingActive(pricing.day12)) out.add("day");
    if (isShiftPricingActive(pricing.night12)) out.add("night");
    if (isShiftPricingActive(pricing.live24)) out.add("24hour");
  }
  return [...out];
}

export function caregiverOffersShiftType(caregiver, shiftType) {
  const wanted = String(shiftType || "").trim().toLowerCase();
  if (!wanted) return true;
  const fromPricing = resolveOfferedShiftTypesFromPricing(caregiver);
  if (fromPricing.length) return fromPricing.includes(wanted);
  const offered = Array.isArray(caregiver?.shiftTypes) && caregiver.shiftTypes.length
    ? caregiver.shiftTypes.map((s) => String(s || "").trim().toLowerCase())
    : caregiver?.shiftType
      ? [String(caregiver.shiftType).trim().toLowerCase()]
      : [];
  if (!offered.length) return false;
  return offered.includes(wanted);
}
